/*  MUI  */
import {
    Alarm,
    MonetizationOn,
    PieChart,
    Search,
    Security
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Grid,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { Permission } from 'application/Shared/Components/Core';
import { TruncatedText } from 'application/Shared/Components/TruncatedText';
import { Response, Route } from 'application/Shared/Helpers';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { withRouter } from 'router/withRouter';

class ReportSelection extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loaded: false,
            options: []
        };

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = async () => {
        const { match } = this.props;
        const { clientId } = match.params;

        const params = { filter: 'report' };

        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/reports/config', {
                cancelToken: this.requests.token(),
                params
            });
            if (!Response.hasResponseData(response)) {
                debug.e('No Reports found', response);
                return;
            }
            const data = Response.getResponseData(response);

            const loadedOptions = data
                .map((option) => ({
                    ...option,
                    key: option.type
                }))
                .filter(
                    // If there is a client id, then we need to filter admin reports (option.partner === false)
                    (option) => (clientId ? !option.partner : option.partner)
                );

            this.setState({ loaded: true, options: loadedOptions });
        } catch (e) {
            debug.e('Unable to load report config', e);
        }
    };

    unload = () => {
        this.requests.cancel();
    };

    searchOptions = (e) => {
        const search = e.target.value.toLowerCase();
        const { options } = this.state;
        options.forEach((_v, k) => {
            options[k].hidden = !(
                (options[k].name + options[k].description)
                    .toLowerCase()
                    .indexOf(search) > -1
            );
        });
        this.setState(options);
    };

    renderChip = (benefit, key) => {
        const { t } = this.props;

        const style = {
            alignItems: 'center',
            borderRadius: '4px',
            justifyContent: 'center',
            marginBottom: '8px',
            marginRight: '8px',
            minWidth: '110px',
            padding: '4px'
        };

        switch (benefit) {
            case 'security':
                return (
                    <Box
                        display="flex"
                        key={`${key}_${benefit}`}
                        sx={{
                            ...style,
                            backgroundColor:
                                'reports.chips.security.backgroundColor',
                            border: '1px solid',
                            borderColor: 'reports.chips.security.borderColor',
                            color: 'reports.chips.security.textColor'
                        }}
                    >
                        <Security
                            fontSize="small"
                            sx={{
                                color: 'reports.chips.security.iconColor',
                                mr: '4px'
                            }}
                        />
                        {t('Common.security')}
                    </Box>
                );
            case 'savings':
                return (
                    <Box
                        display="flex"
                        key={`${key}_${benefit}`}
                        sx={{
                            ...style,
                            backgroundColor:
                                'reports.chips.savings.backgroundColor',
                            border: '1px solid',
                            borderColor: 'reports.chips.savings.borderColor',
                            color: 'reports.chips.savings.textColor'
                        }}
                    >
                        <MonetizationOn
                            fontSize="small"
                            sx={{
                                color: 'reports.chips.savings.iconColor',
                                mr: '4px'
                            }}
                        />
                        {t('Common.savings')}
                    </Box>
                );
            case 'efficiency':
                return (
                    <Box
                        display="flex"
                        key={`${key}_${benefit}`}
                        sx={{
                            ...style,
                            backgroundColor:
                                'reports.chips.efficiency.backgroundColor',
                            border: '1px solid',
                            borderColor: 'reports.chips.efficiency.borderColor',
                            color: 'reports.chips.efficiency.textColor'
                        }}
                    >
                        <Alarm
                            fontSize="small"
                            sx={{
                                color: 'reports.chips.efficiency.iconColor',
                                mr: '4px'
                            }}
                        />
                        {t('Common.efficiency')}
                    </Box>
                );
            case 'research':
                return (
                    <Box
                        display="flex"
                        key={`${key}_${benefit}`}
                        sx={{
                            ...style,
                            backgroundColor:
                                'reports.chips.research.backgroundColor',
                            border: '1px solid',
                            borderColor: 'reports.chips.research.borderColor',
                            color: 'reports.chips.research.textColor'
                        }}
                    >
                        <Search
                            fontSize="small"
                            sx={{
                                color: 'reports.chips.research.iconColor',
                                mr: '4px'
                            }}
                        />
                        {t('Common.research')}
                    </Box>
                );
            case 'trends':
                return (
                    <Box
                        display="flex"
                        key={`${key}_${benefit}`}
                        sx={{
                            ...style,
                            backgroundColor:
                                'reports.chips.trends.backgroundColor',
                            border: '1px solid',
                            borderColor: 'reports.chips.trends.borderColor',
                            color: 'reports.chips.trends.textColor'
                        }}
                    >
                        <PieChart
                            fontSize="small"
                            sx={{
                                color: 'reports.chips.trends.iconColor',
                                mr: '4px'
                            }}
                        />
                        {t('Common.trends')}
                    </Box>
                );
            default:
                return null;
        }
    };

    renderOption = (opt) => {
        const { match, t } = this.props;
        const { hidden } = opt;

        const { clientId, partnerId } = match.params;

        const baseRoute = Route.buildBaseReportsRoute({ clientId, partnerId });
        const route = `${baseRoute}/${opt.type}`;

        if (hidden) {
            return null;
        }

        return (
            <Permission required={opt.permission} key={opt.key}>
                <Grid item xs={12} md={6} lg={4}>
                    <Card
                        variant="outlined"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            justifyContent: 'space-between'
                        }}
                    >
                        <CardHeader
                            title={
                                <Typography variant="h2">{opt.name}</Typography>
                            }
                            style={{ paddingBottom: 0, textAlign: 'left' }}
                        />
                        <CardContent style={{ paddingTop: 0 }}>
                            <Box display="flex" flexDirection="column">
                                <hr />
                                <Box height="70px">
                                    <TruncatedText
                                        maxLines={3}
                                        text={opt.description}
                                    />
                                </Box>
                                <hr />
                                {opt.benefits && (
                                    <Box
                                        display="flex"
                                        width="100%"
                                        flexWrap="wrap"
                                    >
                                        {opt.benefits.map((benefit) =>
                                            this.renderChip(benefit, opt.key)
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                        <CardActions style={{ marginLeft: '8px' }}>
                            <Link
                                to={route}
                                data-trackid={`reports_runReport_${opt.key}_routeTo`}
                            >
                                <Button
                                    data-trackid={`reports_runReport_${opt.key}`}
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                >
                                    {t('Common.run')}
                                </Button>
                            </Link>
                        </CardActions>
                    </Card>
                </Grid>
            </Permission>
        );
    };

    render() {
        const { t } = this.props;
        const { loaded, options } = this.state;

        return (
            <>
                <PageTitle title={t('Reports.report_setup')} />
                {loaded && (
                    <>
                        <Box style={{ marginBottom: '24px' }}>
                            <TextField
                                id="standard-basic"
                                label={t('Common.search')}
                                fullWidth
                                onChange={this.searchOptions}
                            />
                        </Box>
                        <Grid
                            container
                            spacing={3}
                            display="flex"
                            alignItems="stretch"
                        >
                            {options.map((opt) => this.renderOption(opt))}
                        </Grid>
                    </>
                )}
            </>
        );
    }
}

export default withRouter(withTranslation()(ReportSelection));
